/**
 * Task Relations Database Operations
 *
 * CRUD operations for task dependencies and relationships.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { isCompletedStatus } from '@/constants/taskStatuses';

export type RelationType = 'blocks' | 'blocked_by' | 'related_to' | 'duplicates' | 'parent_of' | 'child_of';

export interface TaskRelation {
  id: string;
  sourceTaskId: string;
  targetTaskId: string;
  relationType: RelationType;
  createdAt: string;
  createdBy: string | null;
}

export interface CreateRelationData {
  sourceTaskId: string;
  targetTaskId: string;
  relationType: RelationType;
  createdBy?: string;
}

// Supabase row type for task_relations table
interface SupabaseTaskRelationRow {
  id: string;
  source_task_id: string;
  target_task_id: string;
  relation_type: RelationType;
  created_at: string;
  created_by: string | null;
}

// Convert from Supabase (snake_case) to TaskRelation (camelCase)
function convertRelationFromSupabase(row: SupabaseTaskRelationRow): TaskRelation {
  return {
    id: row.id,
    sourceTaskId: row.source_task_id,
    targetTaskId: row.target_task_id,
    relationType: row.relation_type,
    createdAt: row.created_at,
    createdBy: row.created_by,
  };
}

/**
 * Get all relations for a task (as source or target)
 */
export async function getTaskRelations(taskId: string): Promise<TaskRelation[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('*')
      .or(`source_task_id.eq.${taskId},target_task_id.eq.${taskId}`)
  );

  if (error) throw error;
  return (data || []).map(convertRelationFromSupabase);
}

/**
 * Get all relations where task is the source (outgoing relations)
 */
export async function getOutgoingRelations(taskId: string): Promise<TaskRelation[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('*')
      .eq('source_task_id', taskId)
  );

  if (error) throw error;
  return (data || []).map(convertRelationFromSupabase);
}

/**
 * Get all relations where task is the target (incoming relations)
 */
export async function getIncomingRelations(taskId: string): Promise<TaskRelation[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('*')
      .eq('target_task_id', taskId)
  );

  if (error) throw error;
  return (data || []).map(convertRelationFromSupabase);
}

/**
 * Get blocking tasks (tasks that block this task)
 */
export async function getBlockingTasks(taskId: string): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('source_task_id')
      .eq('target_task_id', taskId)
      .eq('relation_type', 'blocks')
  );

  if (error) throw error;
  return (data || []).map(r => r.source_task_id);
}

/**
 * Get blocked tasks (tasks that this task blocks)
 */
export async function getBlockedTasks(taskId: string): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('target_task_id')
      .eq('source_task_id', taskId)
      .eq('relation_type', 'blocks')
  );

  if (error) throw error;
  return (data || []).map(r => r.target_task_id);
}

/**
 * Get related tasks (non-blocking relations)
 */
export async function getRelatedTasks(taskId: string): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('source_task_id, target_task_id')
      .eq('relation_type', 'related_to')
      .or(`source_task_id.eq.${taskId},target_task_id.eq.${taskId}`)
  );

  if (error) throw error;

  const relatedIds = new Set<string>();
  for (const r of data || []) {
    if (r.source_task_id !== taskId) relatedIds.add(r.source_task_id);
    if (r.target_task_id !== taskId) relatedIds.add(r.target_task_id);
  }
  return Array.from(relatedIds);
}

/**
 * Create a new relation between tasks
 */
export async function createRelation(data: CreateRelationData): Promise<TaskRelation> {
  // Prevent self-relations
  if (data.sourceTaskId === data.targetTaskId) {
    throw new Error('A task cannot have a relation with itself');
  }

  // Check for cycles in blocking relations
  if (data.relationType === 'blocks') {
    const wouldCreateCycle = await detectBlockingCycle(data.sourceTaskId, data.targetTaskId);
    if (wouldCreateCycle) {
      throw new Error('This would create a circular dependency');
    }
  }

  const insertData = {
    source_task_id: data.sourceTaskId,
    target_task_id: data.targetTaskId,
    relation_type: data.relationType,
    created_by: data.createdBy || null,
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .insert(insertData)
      .select()
      .single()
  );

  if (error) {
    if (error.code === '23505') {
      throw new Error('This relation already exists');
    }
    throw error;
  }

  return convertRelationFromSupabase(created);
}

/**
 * Delete a relation
 */
export async function deleteRelation(id: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
  return true;
}

/**
 * Delete a relation by source, target, and type
 */
export async function deleteRelationByTasks(
  sourceTaskId: string,
  targetTaskId: string,
  relationType: RelationType
): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('task_relations')
      .delete()
      .eq('source_task_id', sourceTaskId)
      .eq('target_task_id', targetTaskId)
      .eq('relation_type', relationType)
  );

  if (error) throw error;
  return true;
}

/**
 * Create a blocking relationship (A blocks B)
 */
export async function createBlockingRelation(
  blockingTaskId: string,
  blockedTaskId: string,
  createdBy?: string
): Promise<TaskRelation> {
  return createRelation({
    sourceTaskId: blockingTaskId,
    targetTaskId: blockedTaskId,
    relationType: 'blocks',
    createdBy,
  });
}

/**
 * Create a related_to relationship (bidirectional in meaning)
 */
export async function createRelatedRelation(
  taskId1: string,
  taskId2: string,
  createdBy?: string
): Promise<TaskRelation> {
  return createRelation({
    sourceTaskId: taskId1,
    targetTaskId: taskId2,
    relationType: 'related_to',
    createdBy,
  });
}

/**
 * Create a duplicates relationship
 */
export async function createDuplicateRelation(
  duplicateTaskId: string,
  originalTaskId: string,
  createdBy?: string
): Promise<TaskRelation> {
  return createRelation({
    sourceTaskId: duplicateTaskId,
    targetTaskId: originalTaskId,
    relationType: 'duplicates',
    createdBy,
  });
}

/**
 * Check if creating a blocking relation would create a cycle
 * Uses BFS to detect if there's already a path from blockedTask to blockingTask
 */
async function detectBlockingCycle(blockingTaskId: string, blockedTaskId: string): Promise<boolean> {
  // If blockingTask is already blocked by blockedTask (directly or indirectly), this creates a cycle
  const visited = new Set<string>();
  const queue = [blockedTaskId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    // Get all tasks that this task blocks
    const blockedByThis = await getBlockedTasks(currentId);

    for (const id of blockedByThis) {
      if (id === blockingTaskId) {
        return true; // Cycle detected!
      }
      if (!visited.has(id)) {
        queue.push(id);
      }
    }
  }

  return false;
}

/**
 * Check if a task is blocked (has any unresolved blocking tasks)
 */
export async function isTaskBlocked(taskId: string): Promise<boolean> {
  const blockingTasks = await getBlockingTasks(taskId);
  if (blockingTasks.length === 0) return false;

  // Check if any blocking task is not completed
  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('id, status')
      .in('id', blockingTasks)
  );

  if (error) throw error;

  // Task is blocked if any blocking task is not completed
  return (data || []).some(t => !isCompletedStatus(t.status));
}

/**
 * Get all task IDs that are blocked by uncompleted tasks
 */
export async function getBlockedTaskIds(): Promise<Set<string>> {
  // Get all blocking relations
  const { data: relations, error: relError } = await withRetry(() =>
    supabase
      .from('task_relations')
      .select('source_task_id, target_task_id')
      .eq('relation_type', 'blocks')
  );

  if (relError) throw relError;
  if (!relations || relations.length === 0) return new Set();

  // Get all source task IDs (blocking tasks)
  const sourceIds = [...new Set(relations.map(r => r.source_task_id))];

  // Get their statuses
  const { data: tasks, error: taskError } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('id, status')
      .in('id', sourceIds)
  );

  if (taskError) throw taskError;

  // Find which source tasks are not completed
  const uncompletedSourceIds = new Set(
    (tasks || [])
      .filter(t => !isCompletedStatus(t.status))
      .map(t => t.id)
  );

  // Find all target tasks blocked by uncompleted source tasks
  const blockedIds = new Set<string>();
  for (const r of relations) {
    if (uncompletedSourceIds.has(r.source_task_id)) {
      blockedIds.add(r.target_task_id);
    }
  }

  return blockedIds;
}
